import {
    AssignmentDto,
    BoltChangePhysicalPropertiesDto,
    BoltReadDto,
    ManufacturingDetailsDto,
    MeasurementType,
} from '../_generated/api/tagged-hardware'
import ApiModel from './ApiModel'
import ApiLifeCycleModel from './ApiLifeCycleModel'
import { Ancestor } from './ApiStructureModel'
import Measurement from './Measurement'
import TraceabilityTag from './TraceabilityTag'
import { StructureTypes } from './Structure'
import { buildStructureLink } from '../tools/link'
import { mm } from '../tools/format'
import { _ta, _tf } from '../tools/t'

export const generateBoltName = (bolt: Bolt) => {
    if (!(bolt.material && bolt.diameter && bolt.type)) return ''
    const diameter = _ta(bolt, 'diameter')
    const length = mm(bolt.data.nominalLengthMm)
    const material = _tf('', 'material', bolt.material)
    const type = _ta(bolt, 'type')
    return `${diameter} x ${length} - ${material} ${type}`
}

export const BoltMeasurementProgressTypes = {
    ...MeasurementType,
    Assigned: 'assigned',
    Archived: 'archived',
}
export type BoltMeasurementProgressType =
    (typeof BoltMeasurementProgressTypes)[keyof typeof BoltMeasurementProgressTypes]

interface MeasurementActivity {
    type?: BoltMeasurementProgressType
    date?: Date
    user?: string
}

export default class Bolt extends ApiLifeCycleModel<BoltReadDto> {
    get id() {
        return this.data.id ?? ''
    }

    get name() {
        return generateBoltName(this)
    }

    get assignment() {
        return Assignment.nullable(this.data.assignment)
    }

    get currentTag() {
        return TraceabilityTag.nullable(this.data.currentTag)
    }

    get previousTags() {
        return this.data.previousTags?.map((t) => new TraceabilityTag(t)) ?? []
    }

    get manufacturingDetails() {
        return ManufacturingDetails.nullable(this.data.manufacturingDetails)
    }

    get measurements() {
        return this.data.measurementHistory.map((m) => new Measurement(m))
    }

    get elongationCount() {
        return this.measurements.filter((m) => m.type === MeasurementType.Elongation).length
    }

    get typeSummary(): BoltMeasurementProgressType[] {
        const types = []
        if (this.assignment?.assignedAt) types.push(BoltMeasurementProgressTypes.Assigned)
        if (this.activeReference?.measuredAt) types.push(BoltMeasurementProgressTypes.Reference)
        if (this.latestElongation?.measuredAt) types.push(BoltMeasurementProgressTypes.Elongation)
        return types
    }

    get isAssigned() {
        const assigned = this.assignment?.assignedAt
        return assigned && assigned.getTime() > 0
    }

    get latestActivity(): MeasurementActivity | undefined {
        if (this.isArchived)
            return {
                user: this.archivedBy,
                date: this.archivedAt,
                type: BoltMeasurementProgressTypes.Archived,
            }
        const { assignment, activeReference, latestElongation } = this
        const assignedAt = assignment?.assignedAt
        const referencedAt = activeReference?.measuredAt
        const elongatedAt = latestElongation?.measuredAt

        const activities = [assignedAt, referencedAt, elongatedAt].filter((d) => !!d)
        activities.sort((a, b) => b.getTime() - a.getTime())
        const latest = activities[0]
        if (!latest) return undefined

        // Find the activity from the latest result by comparing time.
        // For date collisions the result will naturally fallback to the order
        //  [assigned, reference, elongation], preferring assigned first.
        switch (latest.getTime()) {
            case assignedAt?.getTime(): {
                return {
                    date: assignedAt,
                    user: assignment?.tenantId,
                    type: BoltMeasurementProgressTypes.Assigned,
                }
            }

            case referencedAt?.getTime(): {
                return {
                    date: referencedAt,
                    user: activeReference?.measuredBy,
                    type: activeReference?.type ?? BoltMeasurementProgressTypes.Reference,
                }
            }

            case elongatedAt?.getTime(): {
                return {
                    date: elongatedAt,
                    user: latestElongation?.measuredBy,
                    type: latestElongation?.type ?? BoltMeasurementProgressTypes.Elongation,
                }
            }

            default:
                return undefined
        }
    }

    get activeReference() {
        return Measurement.nullable(this.data.activeReferenceMeasurement)
    }

    get latestElongation() {
        return Measurement.nullable(this.data.latestElongationMeasurement)
    }

    get pretension() {
        return this.data.pretensionKn ?? 0
    }

    get nominalLength() {
        return this.data.nominalLengthMm ?? 0
    }

    get diameter() {
        return this.data.diameter ?? ''
    }

    get material() {
        return this.data.material ?? ''
    }

    get type() {
        return this.data.type ?? ''
    }

    get referenceLink(): string {
        return buildStructureLink(StructureTypes.Bolt, this.id)
    }

    asPhysicalProperties(): BoltChangePhysicalPropertiesDto {
        const { material, diameter, type, nominalLengthMm, springConstantKnMm } = this.data
        return { material, diameter, type, nominalLengthMm, springConstantKnMm }
    }

    asAncestor(): Ancestor {
        return {
            id: this.id,
            link: this.referenceLink,
            name: this.name,
        }
    }
}

export class Assignment extends ApiModel<AssignmentDto> {
    get tenantId() {
        return this.data.tenant ?? undefined
    }

    get groupId() {
        return this.data.boltConnection ?? ''
    }

    get index() {
        return this.data.index ?? undefined
    }

    get position() {
        const { index } = this
        if (typeof index !== 'number') return undefined
        return (index + 1).toString()
    }

    get assignedAt() {
        const { assignedAt } = this.data
        if (!assignedAt) return undefined
        return new Date(assignedAt)
    }
}

export class ManufacturingDetails extends ApiModel<ManufacturingDetailsDto> {
    get organizationId() {
        return this.data.tenant ?? ''
    }

    get batch() {
        return this.data.batch ?? ''
    }
}
